import { useCallback, useEffect, useState } from 'react';
import { useUser } from '../../../context/UserContext';
import { supabase } from '../../../lib/supabase';
import { uniqueChannelName } from '../../../lib/realtime';
import { operatorSummaryForJob } from '../../../lib/aiJobSummary';
import {
  fetchJobFeedback, postSubmissionNote, submissionNoteState, type FeedbackBatch, type SubmissionNoteState,
} from '../../../lib/clientFeedback';
import type { AiJob } from '../../../lib/supabase.types';

/* The Submit Note state of a job working client feedback: its groups, whether
   the client note is on them as written, and posting it (SubmissionNote.tsx). */

export interface SubmissionNoteControl {
  /** Feedback groups this job works; [] for a job that isn't client feedback. */
  batches: FeedbackBatch[];
  loaded: boolean;
  state: SubmissionNoteState;
  /** The client's app, for the copy ("Pete's Panels CRM"). */
  appLabel: string;
  posting: boolean;
  error: string | null;
  /** Clicking Submitted should ask first: there's a client note and it isn't on the client's feedback as written. */
  needsGate: boolean;
  post: () => Promise<boolean>;
}

export function useSubmissionNote(job: AiJob | null): SubmissionNoteControl {
  const { user } = useUser();
  const jobId = job?.id ?? null;
  const [rows, setRows] = useState<{ jobId: string; batches: FeedbackBatch[] } | null>(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      setRows({ jobId, batches: await fetchJobFeedback(jobId) });
    } catch {
      setRows({ jobId, batches: [] });
    }
  }, [jobId]);

  useEffect(() => {
    setError(null);
    void load();
  }, [load]);

  // A post from another tab, or the group changing under the job, shows here too.
  useEffect(() => {
    if (!jobId) return;
    const ch = supabase
      .channel(uniqueChannelName(`job-feedback-${jobId}`))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_feedback_batches', filter: `ai_job_id=eq.${jobId}` }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [jobId, load]);

  const loaded = !!jobId && rows?.jobId === jobId;
  const batches = loaded ? rows.batches : [];
  const clientNote = job ? operatorSummaryForJob(job)?.approval?.client_note ?? null : null;
  const state: SubmissionNoteState = loaded ? submissionNoteState(batches, clientNote) : { kind: 'none' };
  const needsGate = loaded && !!clientNote?.trim() && (state.kind === 'missing' || state.kind === 'changed');

  const post = async () => {
    if (!jobId || !clientNote?.trim()) return false;
    setPosting(true);
    setError(null);
    try {
      await postSubmissionNote(jobId, clientNote, user?.name || user?.email || 'Bullfinch');
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the note');
      return false;
    } finally {
      setPosting(false);
    }
  };

  return { batches, loaded, state, appLabel: batches[0]?.source?.label ?? 'the client’s app', posting, error, needsGate, post };
}

